import { Option } from "../common/SelectBase";

/** Outcome of matching pasted entries against the dropdown's options. */
export interface PasteMatchResult {
  /** Option values that matched an entry, deduplicated, in paste order. */
  matchedValues: string[];
  /** Entries that matched no option, in paste order. */
  unmatched: string[];
  /** Number of distinct pasted entries considered. */
  total: number;
}

/**
 * Excel copies a column as newline-separated cells and a row as tab-separated
 * ones; a single-line paste is an ordinary search term, not a list.
 */
export function isListPaste(text: string): boolean {
  return /[\n\r\t]/.test(text);
}

/** Mirrors the backend's dimension-value equality, so UI and server agree. */
function normalizeForMatch(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Splits pasted text into entries, dropping empties and duplicates. */
export function splitPastedList(text: string): string[] {
  const seen = new Set<string>();
  const entries: string[] = [];
  // Never split on commas — option values legally contain them ("Copenhagen, Fields").
  for (const raw of text.split(/[\n\r\t]+/)) {
    const entry = raw.trim();
    const key = normalizeForMatch(entry);
    if (entry && !seen.has(key)) {
      seen.add(key);
      entries.push(entry);
    }
  }
  return entries;
}

/** Resolves entries against the options by label or value; misses are reported, never created. */
export function matchPastedEntries(
  entries: string[],
  options: Option[]
): PasteMatchResult {
  const valueByKey = new Map<string, string>();
  for (const option of options) {
    for (const key of [
      normalizeForMatch(option.label),
      normalizeForMatch(option.value)
    ]) {
      if (!valueByKey.has(key)) valueByKey.set(key, option.value);
    }
  }

  const matched = new Set<string>();
  const unmatched: string[] = [];
  for (const entry of entries) {
    const value = valueByKey.get(normalizeForMatch(entry));
    if (value === undefined) unmatched.push(entry);
    else matched.add(value);
  }

  return {
    matchedValues: Array.from(matched),
    unmatched,
    total: entries.length
  };
}

/** One-line outcome for the dropdown. */
export function formatPasteReport({
  unmatched,
  total
}: PasteMatchResult): string {
  const summary = `Selected ${total - unmatched.length} of ${total} pasted values`;
  if (unmatched.length === 0) return summary;
  // Only a few misses are named: the report sits in the popover, above the list.
  const named = unmatched.slice(0, 5);
  const rest = unmatched.length - named.length;
  return `${summary}. Not found: ${named.join(", ")}${rest > 0 ? `, and ${rest} more` : ""}`;
}
